import React, { useState, useEffect } from "react";
import { Button, Label, TextInput, FileInput } from "flowbite-react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";
import axios from "axios";
import "../style/Setup.css";

const Setup = ({ onComplete }) => {
  const navigate = useNavigate();
  const baseURL = process.env.REACT_APP_BASE_URL;
  const [formData, setFormData] = useState({
    company_name: "",
    logo: null,
    initial_profit: "",
    theme_color: "",
    username: "",
    password: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);

  // Validations
  const validateForm = () => {
    const newErrors = {};
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

    // Check each field
    if (!formData.company_name.trim())
      newErrors.company_name = "Company name is required.";
    if (!formData.logo) newErrors.logo = "Logo file is required.";
    if (!formData.initial_profit.trim())
      newErrors.initial_profit = "Initial profit is required.";
    if (!formData.theme_color)
      newErrors.theme_color = "Theme color is required.";
    if (!formData.username.trim()) newErrors.username = "Username is required.";
    if (!formData.password.trim()) newErrors.password = "Password is required.";
    else if (!passwordRegex.test(formData.password)) {
      newErrors.password =
        "Password must be at least 8 characters, with 1 uppercase, 1 lowercase, 1 digit, and 1 special character.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    const isFilled = Object.values(formData).every((field) => field !== "");
    setIsSubmitEnabled(isFilled && validateForm());
  }, [formData]);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    setFormData({
      ...formData,
      [name]: files ? files[0] : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Submit clicked'); 
    if (!validateForm()) return;

    try {
      const data = new FormData();
      data.append("company_name", formData.company_name);
      if (formData.logo) {
        data.append("logo", formData.logo);
      }
      data.append("initial_profit", formData.initial_profit);
      data.append("theme_color", formData.theme_color);
      data.append("username", formData.username);
      data.append("password", formData.password);

      const response = await axios.post(`${baseURL}/insertSetup`, data);

      if (response.status === 201) {
        Swal.fire({
          icon: "success",
          text: "Setup completed successfully!",
          timer: 2000,
          showConfirmButton: false,
        });

        // Store setup completion flag in sessionStorage
        sessionStorage.setItem("setupCompleted", formData.username);
        if (onComplete) {
          onComplete();
        }

        navigate("/");
      } else {
        Swal.fire({
          icon: "error",
          text: response.data.error || "Setup failed. Please try again.",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error("Setup error:", error);
      Swal.fire({
        icon: "error",
        text: "An error occurred. Please try again later.",
        timer: 2000,
        showConfirmButton: false,
      });
    }
  };

  return (
    <div className="setup-container">
      <div className="contact-box">
        <div className="setup-card">
          <h2 className="text-center text-2xl font-semibold mb-4">
            Setup Page
          </h2>
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-field">
                <Label htmlFor="company_name" value="Company Name" />
                <TextInput
                  name="company_name"
                  id="company_name"
                  type="text"
                  required
                  onChange={handleInputChange}
                  color={errors.company_name ? "failure" : undefined}
                />
                {errors.company_name && (
                  <p className="text-red-500 text-sm">{errors.company_name}</p>
                )}
              </div>

              <div className="form-field">
                <Label htmlFor="theme_color" value="Theme Color" />
                <TextInput
                  name="theme_color"
                  id="theme_color"
                  type="color"
                  required
                  onChange={handleInputChange}
                  color={errors.theme_color ? "failure" : undefined}
                />
                {errors.theme_color && (
                  <p className="text-red-500 text-sm">{errors.theme_color}</p>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <Label htmlFor="logo" value="Logo" />
                <FileInput
                  name="logo"
                  id="logo"
                  required
                  onChange={handleInputChange}
                  color={errors.logo ? "failure" : undefined}
                />
                {errors.logo && (
                  <p className="text-red-500 text-sm">{errors.logo}</p>
                )}
              </div>

              <div className="form-field">
                <Label htmlFor="initial_profit" value="Initial Profit" />
                <TextInput
                  name="initial_profit"
                  id="initial_profit"
                  type="number"
                  placeholder="value in percentage (e.g. 20, 30)"
                  required
                  onChange={handleInputChange}
                  color={errors.initial_profit ? "failure" : undefined}
                />
                {errors.initial_profit && (
                  <p className="text-red-500 text-sm">
                    {errors.initial_profit}
                  </p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="username" value="Username" />
              <TextInput
                name="username"
                id="username"
                type="text"
                required
                onChange={handleInputChange}
                color={errors.username ? "failure" : undefined}
              />
              {errors.username && (
                <p className="text-red-500 text-sm">{errors.username}</p>
              )}
            </div>

            <div>
              <Label htmlFor="password" value="Password" />
              <TextInput
                name="password"
                id="password"
                type="password"
                required
                onChange={handleInputChange}
                color={errors.password ? "failure" : undefined}
              />
              {errors.password && (
                <p className="text-red-500 text-sm">{errors.password}</p>
              )}
            </div>

            <div className="flex justify-center gap-4">
              <Button type="submit" color="warning" disabled={!isSubmitEnabled}>
                Submit
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Setup;
